import React, { createContext, useContext, useEffect, useState } from "react";

export const translations = {
  en: {
    announcement:
      "Telecomp Solutions LLC est 2000. Refurbished, used and off-lease computer products and peripheral sales.",
    nav: {
      home: "HOME",
      about: "ABOUT US",
      catalog: "INVENTORY",
      products: "PRODUCTS",
      services: "SERVICES",
      contact: "CONTACT",
      subscribe: "JOIN MAILING LIST",
    },
    getQuote: "Get a Quote",
    hero: {
      badge: "Trusted Since 2000",
      headlineParts: [
        { text: "Quality " },
        { text: "Certified Technology", hl: true },
        { text: " Refurbisher" },
      ],
      sub: "Laptops, desktops, small form factor and micro PCs, monitors, components and peripherals — professionally tested, data-wiped and ready to ship worldwide.",
      viewProducts: "Browse Inventory",
      learnMore: "Learn More",
      certifiedTitle: "Tested & Certified",
      certifiedSub: "Data-wiped & tested",
    },
    stats: [
      { value: "25+", label: "Years in Business" },
      { value: "250K+", label: "Assets Resold" },
      { value: "100%", label: "Data Wiped & Tested" },
      { value: "Worldwide", label: "Shipping" },
    ],
    about: {
      eyebrow: "About Us",
      title: "A Trusted Name in Refurbished IT Since 2000",
      p1: "Telecomp Solutions, LLC is a Florida-registered company headquartered at 8243 NW 66th St, Miami, Florida 33166. For over two decades we've specialized in refurbished, used and off-lease computer products — processing bulk inventory of laptops, desktops, monitors and small form factor PCs.",
      p2: "We're registered with the Florida Department of Environmental Protection and the U.S. Environmental Protection Agency, ensuring every device is handled responsibly from intake to resale.",
      bullets: [
        "Bulk inventory processed & graded in-house",
        "Certified data wiping on every device",
        "EPA & Florida DEP registered operations",
        "International and domestic direct shipping",
      ],
      cta: "View Our Inventory",
      estLabel: "Est. 2000",
      estCity: "Miami, Florida",
    },
    products: {
      eyebrow: "Our Products",
      title: "What We Offer",
      sub: "A wide selection of refurbished computers and components — explore the product lines we carry.",
      partsEyebrow: "Components & More",
      partsTitle: "Parts & Accessories",
    },
    catalog: {
      title: "Inventory Catalog",
      subtitle:
        "Browse our in-stock refurbished computers, monitors and parts. Tap any item to request a quote on WhatsApp.",
      moreInventoryNote:
        "Don't see what you need? We may have additional inventory that isn't listed here yet — message us on WhatsApp and we'll check our warehouse for you.",
      dl: {
        pageTitle: "Our Inventory List",
        pageSubtitle:
          "Download our latest inventory as a file. Questions or need a quote? Message us on WhatsApp.",
        homeTitle: "Get Our Full Inventory List",
        homeSubtitle:
          "Download our complete, up-to-date inventory in one file — everything we have in stock.",
        button: "Download Inventory List",
        updated: "Last updated",
        noFile:
          "Our inventory list isn't posted right now. Message us on WhatsApp and we'll send it over.",
        contactWhatsApp: "Ask on WhatsApp",
      },
      searchPlaceholder: "Search products…",
      searchBtn: "Search",
      all: "All",
      requestQuote: "Request Quote",
      viewDetails: "View Details",
      noResults: "No products found. Try a different category or search.",
      loadMore: "Load More",
      loading: "Loading products…",
      specs: "Specifications",
      callForPrice: "Contact for price",
      inStock: "In stock",
      downloadPdf: "Inventory list (PDF)",
      downloadingPdf: "Preparing…",
      downloadSheet: "Spreadsheet",
      viewGrid: "Grid",
      viewList: "List",
      colProduct: "Product",
      colCategory: "Category",
      colCondition: "Condition",
      colPrice: "Price",
      colStock: "Quantity",
      resultsCount: (n) => `${n} ${n === 1 ? "product" : "products"}`,
      quoteMessage: (name) =>
        `Hi! I'm interested in this item: ${name}. Is it available?`,
    },
    liveInventory: {
      eyebrow: "Live Inventory",
      title: "In Stock & Ready to Ship",
      sub: "Real units from our warehouse, updated in real time. Tap any item for specs and a quick WhatsApp quote.",
      viewAll: "View Full Inventory",
    },
    categories: {
      monitors: "Monitors",
      desktops: "Desktops",
      laptops: "Laptops",
      notebooks: "Notebooks",
      sff: "Small Form Factor",
      towers: "Towers",
      micros: "Micros",
    },
    parts: {
      memory: "Memory (RAM)",
      storage: "Hard Drives & SSDs",
      keyboards: "Keyboards",
      mice: "Mice",
      "power-cables": "Power Cables",
      "ac-adapters": "AC Adapters",
    },
    partsDesc: {
      memory: "DDR3 · DDR4 · 8 / 16 / 32GB",
      storage: "SSD · Mechanical · M.2 · SATA · NVMe",
      keyboards: "USB & wireless",
      mice: "Optical & wireless",
      "power-cables": "Standard & regional cords",
      "ac-adapters": "OEM & universal",
    },
    services: {
      eyebrow: "What We Do",
      title: "Our Services",
      sub: "From bulk refurbishing to secure data handling and worldwide delivery — full-lifecycle support for refurbished technology.",
      items: {
        refurbishing: {
          title: "Bulk Refurbishing",
          desc: "We process, test and grade large volumes of off-lease laptops, desktops and monitors to like-new working condition.",
        },
        "data-wiping": {
          title: "Certified Data Wiping",
          desc: "Every device is securely sanitized to industry standards, ensuring no data is ever left behind.",
        },
        wholesale: {
          title: "Wholesale & Remarketing",
          desc: "Competitive volume pricing for resellers and businesses, backed by reliable inventory and reporting.",
        },
        configuration: {
          title: "Custom Configuration",
          desc: "Need specific specs? We configure memory, storage and operating systems to match your requirements.",
        },
        disposition: {
          title: "IT Asset Disposition",
          desc: "Responsible, EPA-registered handling and recycling of retired technology assets.",
        },
        shipping: {
          title: "Worldwide Shipping",
          desc: "Secure, insured shipping direct to your location — domestically and internationally.",
        },
      },
    },
    features: {
      shipping: {
        title: "Secure Shipping",
        desc: "International and direct to your location",
      },
      payments: {
        title: "Flexible Payments",
        desc: "Wire Transfer, ACH, Zelle and Credit Card",
      },
      support: {
        title: "24/7 Support",
        desc: "We have everything you need",
      },
    },
    contact: {
      eyebrow: "Get In Touch",
      title: "Contact Us",
      sub: "The fastest way to reach us is on WhatsApp — message us for quotes, availability or any questions.",
      labels: { location: "Location", phone: "Phone", email: "Email", hours: "Hours" },
      values: { location: "8243 NW 66th St, Miami, Florida 33166", hours: "Mon – Fri: 9:00 AM – 6:00 PM EST" },
      waTitle: "Message Us on WhatsApp",
      waText:
        "Chat directly with our team for real-time answers on pricing, inventory and bulk orders. Tap a quick option below to start.",
      quickMessages: [
        "Hi! I'd like a quote on bulk refurbished computers.",
        "Hi! Do you have laptops available right now?",
        "Hi! I have a general question about your products.",
      ],
      startChat: "Start a Chat",
      callUs: "Call Us",
      emailUs: "Email Us",
    },
    whatsapp: {
      defaultMessage:
        "Hello Tele-Comp Solutions! I'd like to ask about your refurbished inventory.",
      chatWith: "Chat with us",
    },
    footer: {
      desc: "Telecomp Solutions, LLC is a Florida registered company established in June of 2000, headquartered at 8243 NW 66th St, Miami, Florida 33166 — specializing in refurbished, used and off-lease computer products.",
      quickLinks: "Quick Links",
      getInTouch: "Get In Touch",
      locationVal: "Miami, Florida",
      rights: "All Rights Reserved.",
      tagline: "Refurbished. Reliable. Responsible.",
      privacy: "Privacy Policy",
      terms: "Terms & Conditions",
    },
    subscribe: {
      eyebrow: "Newsletter",
      title: "Join our mailing list",
      sub: "Get new arrivals, clearance deals and bulk reseller pricing delivered straight to your inbox.",
      name: "Your name",
      email: "Your email address",
      button: "Subscribe",
      sending: "Subscribing…",
      success: "You're on the list! Thanks for subscribing.",
      error: "Couldn't subscribe right now. Please try again.",
      invalidEmail: "Please enter a valid email address.",
      privacyNote: "We respect your privacy. Unsubscribe anytime.",
    },
    legal: {
      back: "← Back to Home",
      lastUpdated: "Last updated:",
      disclaimer:
        "This document is provided as a general template and should be reviewed by a qualified attorney before publication to ensure it meets your specific legal requirements.",
      contactLine: (email, phone, addr) =>
        `Questions? Reach us at ${email} or ${phone}, ${addr}.`,
      privacy: {
        title: "Privacy Policy",
        sections: [
          {
            title: "Introduction",
            body: "Tele-Comp Solutions respects your privacy. This Privacy Policy explains how we collect, use, and protect information when you visit our website or contact us.",
          },
          {
            title: "Information We Collect",
            body: "We may collect information you voluntarily provide when you contact us by phone, email, or WhatsApp — such as your name, contact details, and the contents of your message. We may also collect basic, non-identifying usage data through standard web analytics.",
          },
          {
            title: "How We Use Information",
            body: "Information is used solely to respond to your inquiries, provide quotes, fulfill orders, and improve our services. We do not sell your personal information.",
          },
          {
            title: "Third-Party Services",
            body: "We use WhatsApp for customer communication and embedded mapping services for location display. Your use of these services is subject to their respective privacy policies.",
          },
          {
            title: "Data Security",
            body: "We take reasonable measures to protect your information. As part of our operations, all refurbished devices undergo certified data sanitization before resale.",
          },
        ],
      },
      terms: {
        title: "Terms & Conditions",
        sections: [
          {
            title: "Acceptance of Terms",
            body: "By accessing and using this website, you accept and agree to be bound by these Terms & Conditions. If you do not agree, please do not use the site.",
          },
          {
            title: "Products & Descriptions",
            body: "We sell refurbished, used, and off-lease computer products and peripherals. We strive for accuracy in product descriptions and imagery, but specifications and availability may vary.",
          },
          {
            title: "Pricing & Payment",
            body: "Pricing is subject to change without notice. We accept wire transfer, ACH, Zelle and credit card. Quotes are valid for the period stated at the time of issue.",
          },
          {
            title: "Shipping",
            body: "We offer secure domestic and international shipping. Delivery times are estimates and may vary based on destination and carrier.",
          },
          {
            title: "Warranty & Returns",
            body: "Eligible products may include a limited warranty as specified at the time of sale. Please contact us regarding warranty claims or return requests.",
          },
          {
            title: "Limitation of Liability",
            body: "To the fullest extent permitted by law, Tele-Comp Solutions shall not be liable for any indirect or consequential damages arising from the use of our products or website.",
          },
        ],
      },
    },
  },

  es: {
    announcement:
      "Telecomp Solutions LLC desde el 2000. Venta de equipos de cómputo y periféricos reacondicionados, usados y de arrendamiento.",
    nav: {
      home: "INICIO",
      about: "NOSOTROS",
      catalog: "INVENTARIO",
      products: "PRODUCTOS",
      services: "SERVICIOS",
      contact: "CONTACTO",
      subscribe: "LISTA DE CORREO",
    },
    getQuote: "Solicitar Cotización",
    hero: {
      badge: "De Confianza Desde el 2000",
      headlineParts: [
        { text: "Reacondicionador de " },
        { text: "Tecnología Certificada", hl: true },
        { text: " de Calidad" },
      ],
      sub: "Laptops, computadoras de escritorio, mini PCs de formato reducido y micro PCs, monitores, componentes y periféricos — probados profesionalmente, con datos borrados y listos para enviar a todo el mundo.",
      viewProducts: "Ver Inventario",
      learnMore: "Conócenos",
      certifiedTitle: "Probado y Certificado",
      certifiedSub: "Datos borrados y probados",
    },
    stats: [
      { value: "25+", label: "Años en el Negocio" },
      { value: "250K+", label: "Activos Revendidos" },
      { value: "100%", label: "Datos Borrados y Probados" },
      { value: "Mundial", label: "Envíos" },
    ],
    about: {
      eyebrow: "Nosotros",
      title: "Un Nombre de Confianza en TI Reacondicionada Desde el 2000",
      p1: "Telecomp Solutions, LLC es una empresa registrada en Florida con sede en 8243 NW 66th St, Miami, Florida 33166. Durante más de dos décadas nos hemos especializado en equipos de cómputo reacondicionados, usados y de arrendamiento — procesando inventario a granel de laptops, computadoras de escritorio, monitores y mini PCs.",
      p2: "Estamos registrados ante el Departamento de Protección Ambiental de Florida y la Agencia de Protección Ambiental de los EE. UU., garantizando que cada equipo se maneje de forma responsable desde su recepción hasta su reventa.",
      bullets: [
        "Inventario a granel procesado y clasificado internamente",
        "Borrado de datos certificado en cada equipo",
        "Operaciones registradas ante EPA y Florida DEP",
        "Envío directo nacional e internacional",
      ],
      cta: "Ver Nuestro Inventario",
      estLabel: "Desde 2000",
      estCity: "Miami, Florida",
    },
    products: {
      eyebrow: "Nuestros Productos",
      title: "Lo Que Ofrecemos",
      sub: "Una amplia selección de computadoras y componentes reacondicionados — explore las líneas de productos que manejamos.",
      partsEyebrow: "Componentes y Más",
      partsTitle: "Partes y Accesorios",
    },
    catalog: {
      title: "Catálogo de Inventario",
      subtitle:
        "Explore nuestras computadoras, monitores y partes reacondicionadas en existencia. Toque cualquier artículo para solicitar una cotización por WhatsApp.",
      moreInventoryNote:
        "¿No encuentra lo que busca? Es posible que tengamos inventario adicional que aún no aparece aquí — escríbanos por WhatsApp y revisaremos nuestro almacén para usted.",
      dl: {
        pageTitle: "Nuestra Lista de Inventario",
        pageSubtitle:
          "Descargue nuestro inventario más reciente en un archivo. ¿Preguntas o necesita una cotización? Escríbanos por WhatsApp.",
        homeTitle: "Obtenga Nuestra Lista de Inventario Completa",
        homeSubtitle:
          "Descargue nuestro inventario completo y actualizado en un solo archivo — todo lo que tenemos en existencia.",
        button: "Descargar Lista de Inventario",
        updated: "Última actualización",
        noFile:
          "Nuestra lista de inventario no está publicada en este momento. Escríbanos por WhatsApp y se la enviaremos.",
        contactWhatsApp: "Preguntar por WhatsApp",
      },
      searchPlaceholder: "Buscar productos…",
      searchBtn: "Buscar",
      all: "Todos",
      requestQuote: "Solicitar Cotización",
      viewDetails: "Ver Detalles",
      noResults: "No se encontraron productos. Pruebe otra categoría o búsqueda.",
      loadMore: "Cargar Más",
      loading: "Cargando productos…",
      specs: "Especificaciones",
      callForPrice: "Consultar precio",
      inStock: "En existencia",
      downloadPdf: "Lista de inventario (PDF)",
      downloadingPdf: "Preparando…",
      downloadSheet: "Hoja de cálculo",
      viewGrid: "Cuadrícula",
      viewList: "Lista",
      colProduct: "Producto",
      colCategory: "Categoría",
      colCondition: "Condición",
      colPrice: "Precio",
      colStock: "Cantidad",
      resultsCount: (n) => `${n} ${n === 1 ? "producto" : "productos"}`,
      quoteMessage: (name) =>
        `¡Hola! Me interesa este artículo: ${name}. ¿Está disponible?`,
    },
    liveInventory: {
      eyebrow: "Inventario en Vivo",
      title: "En Existencia y Listo para Enviar",
      sub: "Unidades reales de nuestro almacén, actualizadas en tiempo real. Toque cualquier artículo para ver especificaciones y cotizar por WhatsApp.",
      viewAll: "Ver Todo el Inventario",
    },
    categories: {
      monitors: "Monitores",
      desktops: "Computadoras de Escritorio",
      laptops: "Laptops",
      notebooks: "Notebooks",
      sff: "Formato Reducido (SFF)",
      towers: "Torres",
      micros: "Micros",
    },
    parts: {
      memory: "Memoria (RAM)",
      storage: "Discos Duros y SSD",
      keyboards: "Teclados",
      mice: "Ratones",
      "power-cables": "Cables de Poder",
      "ac-adapters": "Adaptadores AC",
    },
    partsDesc: {
      memory: "DDR3 · DDR4 · 8 / 16 / 32GB",
      storage: "SSD · Mecánicos · M.2 · SATA · NVMe",
      keyboards: "USB e inalámbricos",
      mice: "Ópticos e inalámbricos",
      "power-cables": "Cables estándar y regionales",
      "ac-adapters": "OEM y universales",
    },
    services: {
      eyebrow: "Lo Que Hacemos",
      title: "Nuestros Servicios",
      sub: "Desde el reacondicionamiento a granel hasta el manejo seguro de datos y la entrega mundial — soporte de ciclo de vida completo para tecnología reacondicionada.",
      items: {
        refurbishing: {
          title: "Reacondicionamiento a Granel",
          desc: "Procesamos, probamos y clasificamos grandes volúmenes de laptops, computadoras y monitores de arrendamiento dejándolos como nuevos.",
        },
        "data-wiping": {
          title: "Borrado de Datos Certificado",
          desc: "Cada equipo se sanitiza de forma segura según los estándares de la industria, garantizando que no quede ningún dato.",
        },
        wholesale: {
          title: "Mayoreo y Remarketing",
          desc: "Precios competitivos por volumen para revendedores y empresas, respaldados por inventario y reportes confiables.",
        },
        configuration: {
          title: "Configuración Personalizada",
          desc: "¿Necesita especificaciones particulares? Configuramos memoria, almacenamiento y sistemas operativos según sus requerimientos.",
        },
        disposition: {
          title: "Disposición de Activos de TI",
          desc: "Manejo y reciclaje responsable y registrado ante la EPA de los activos tecnológicos retirados.",
        },
        shipping: {
          title: "Envío Mundial",
          desc: "Envío seguro y asegurado directo a su ubicación — nacional e internacional.",
        },
      },
    },
    features: {
      shipping: {
        title: "Envío Seguro",
        desc: "Internacional y directo a su ubicación",
      },
      payments: {
        title: "Pagos Flexibles",
        desc: "Transferencia bancaria, ACH, Zelle y tarjeta de crédito",
      },
      support: {
        title: "Soporte 24/7",
        desc: "Tenemos todo lo que necesita",
      },
    },
    contact: {
      eyebrow: "Contáctenos",
      title: "Contáctenos",
      sub: "La forma más rápida de contactarnos es por WhatsApp — escríbanos para cotizaciones, disponibilidad o cualquier pregunta.",
      labels: { location: "Ubicación", phone: "Teléfono", email: "Correo", hours: "Horario" },
      values: { location: "8243 NW 66th St, Miami, Florida 33166", hours: "Lun – Vie: 9:00 AM – 6:00 PM EST" },
      waTitle: "Escríbenos por WhatsApp",
      waText:
        "Chatea directamente con nuestro equipo para respuestas en tiempo real sobre precios, inventario y pedidos a granel. Toca una opción rápida para comenzar.",
      quickMessages: [
        "¡Hola! Me gustaría una cotización de computadoras reacondicionadas a granel.",
        "¡Hola! ¿Tienen laptops disponibles ahora?",
        "¡Hola! Tengo una pregunta general sobre sus productos.",
      ],
      startChat: "Iniciar Chat",
      callUs: "Llámanos",
      emailUs: "Escríbenos",
    },
    whatsapp: {
      defaultMessage:
        "¡Hola Tele-Comp Solutions! Me gustaría preguntar sobre su inventario reacondicionado.",
      chatWith: "Chatea con nosotros",
    },
    footer: {
      desc: "Telecomp Solutions, LLC es una empresa registrada en Florida establecida en junio del 2000, con sede en 8243 NW 66th St, Miami, Florida 33166 — especializada en equipos de cómputo reacondicionados, usados y de arrendamiento.",
      quickLinks: "Enlaces Rápidos",
      getInTouch: "Contáctenos",
      locationVal: "Miami, Florida",
      rights: "Todos los Derechos Reservados.",
      tagline: "Reacondicionado. Confiable. Responsable.",
      privacy: "Política de Privacidad",
      terms: "Términos y Condiciones",
    },
    subscribe: {
      eyebrow: "Boletín",
      title: "Únase a nuestra lista de correo",
      sub: "Reciba novedades, liquidaciones y precios por volumen para revendedores directamente en su correo.",
      name: "Su nombre",
      email: "Su correo electrónico",
      button: "Suscribirse",
      sending: "Suscribiendo…",
      success: "¡Está en la lista! Gracias por suscribirse.",
      error: "No se pudo suscribir en este momento. Inténtelo de nuevo.",
      invalidEmail: "Ingrese un correo electrónico válido.",
      privacyNote: "Respetamos su privacidad. Cancele cuando quiera.",
    },
    legal: {
      back: "← Volver al Inicio",
      lastUpdated: "Última actualización:",
      disclaimer:
        "Este documento se proporciona como una plantilla general y debe ser revisado por un abogado calificado antes de su publicación para garantizar que cumpla con sus requisitos legales específicos.",
      contactLine: (email, phone, addr) =>
        `¿Preguntas? Contáctenos en ${email} o ${phone}, ${addr}.`,
      privacy: {
        title: "Política de Privacidad",
        sections: [
          {
            title: "Introducción",
            body: "Tele-Comp Solutions respeta su privacidad. Esta Política de Privacidad explica cómo recopilamos, usamos y protegemos la información cuando visita nuestro sitio web o nos contacta.",
          },
          {
            title: "Información que Recopilamos",
            body: "Podemos recopilar la información que usted proporcione voluntariamente cuando nos contacta por teléfono, correo o WhatsApp — como su nombre, datos de contacto y el contenido de su mensaje. También podemos recopilar datos básicos de uso no identificables mediante analíticas web estándar.",
          },
          {
            title: "Cómo Usamos la Información",
            body: "La información se utiliza únicamente para responder a sus consultas, brindar cotizaciones, procesar pedidos y mejorar nuestros servicios. No vendemos su información personal.",
          },
          {
            title: "Servicios de Terceros",
            body: "Usamos WhatsApp para la comunicación con clientes y servicios de mapas integrados para mostrar la ubicación. El uso de estos servicios está sujeto a sus respectivas políticas de privacidad.",
          },
          {
            title: "Seguridad de los Datos",
            body: "Tomamos medidas razonables para proteger su información. Como parte de nuestras operaciones, todos los equipos reacondicionados pasan por una sanitización de datos certificada antes de su reventa.",
          },
        ],
      },
      terms: {
        title: "Términos y Condiciones",
        sections: [
          {
            title: "Aceptación de los Términos",
            body: "Al acceder y utilizar este sitio web, usted acepta quedar sujeto a estos Términos y Condiciones. Si no está de acuerdo, por favor no utilice el sitio.",
          },
          {
            title: "Productos y Descripciones",
            body: "Vendemos equipos de cómputo y periféricos reacondicionados, usados y de arrendamiento. Procuramos la exactitud en las descripciones e imágenes de los productos, pero las especificaciones y la disponibilidad pueden variar.",
          },
          {
            title: "Precios y Pago",
            body: "Los precios están sujetos a cambios sin previo aviso. Aceptamos transferencia bancaria, ACH, Zelle y tarjeta de crédito. Las cotizaciones son válidas por el período indicado al momento de su emisión.",
          },
          {
            title: "Envíos",
            body: "Ofrecemos envíos seguros nacionales e internacionales. Los tiempos de entrega son estimados y pueden variar según el destino y el transportista.",
          },
          {
            title: "Garantía y Devoluciones",
            body: "Los productos elegibles pueden incluir una garantía limitada según se especifique al momento de la venta. Contáctenos para reclamos de garantía o solicitudes de devolución.",
          },
          {
            title: "Limitación de Responsabilidad",
            body: "En la máxima medida permitida por la ley, Tele-Comp Solutions no será responsable por daños indirectos o consecuentes derivados del uso de nuestros productos o sitio web.",
          },
        ],
      },
    },
  },
};

const LangContext = createContext({ lang: "en", setLang: () => {}, t: translations.en });

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    const saved = localStorage.getItem("tcs_lang");
    if (saved === "en" || saved === "es") return saved;
    const nav = (navigator.language || navigator.userLanguage || "en").toLowerCase();
    return nav.startsWith("es") ? "es" : "en";
  });

  const setLang = (l) => {
    setLangState(l);
    localStorage.setItem("tcs_lang", l);
    document.documentElement.lang = l;
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = { lang, setLang, t: translations[lang] };
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);

export const LanguageSwitcher = ({ dark = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      className={`inline-flex items-center rounded-full p-0.5 ${
        dark ? "bg-white/10" : "bg-slate-100"
      }`}
    >
      {["en", "es"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          aria-label={`Switch to ${l === "en" ? "English" : "Spanish"}`}
          className={`px-2.5 py-1 text-xs font-bold rounded-full transition-colors ${
            lang === l
              ? "bg-green-600 text-white"
              : dark
              ? "text-slate-300 hover:text-white"
              : "text-slate-600 hover:text-slate-900"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};
